import React, { useEffect, useRef, useState } from "react";
import { X, Gift, Check } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { PRACTICE, ASSETS } from "@/data/site";

const STORAGE_KEY = "sol.giftPopup.shown";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BOOKING_URL =
  "https://book2.getweave.com/7af65575-fcc5-4641-b267-152f93bc72a7/request-appointment?source=WEBSITE";
const OPEN_DELAY_MS = 5000; // Show 5s after landing (once per session)

export default function ExitIntentPopup() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errMsg, setErrMsg] = useState("");
  const armedRef = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    const trigger = () => {
      if (armedRef.current) return;
      armedRef.current = true;
      setOpen(true);
      sessionStorage.setItem(STORAGE_KEY, "1");
    };

    const timer = setTimeout(trigger, OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) return;
    setStatus("sending");
    setErrMsg("");
    try {
      const res = await fetch(`${BACKEND}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          preferred_time: "",
          service: "Amazon Gift Card - New Patient",
          message:
            "Requested the $20 Amazon gift card new-patient offer via popup.",
          consent: true,
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
      // Open Weave booking flow in a new tab so the user completes the reservation.
      try {
        window.open(BOOKING_URL, "_blank", "noopener,noreferrer");
      } catch (_) {
        /* popup blockers ignored — success state still shown */
      }
    } catch (err) {
      setStatus("error");
      setErrMsg(err.message || "Something went wrong");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="exit-intent-popup"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-[fadeIn_.25s_ease-out]"
      onClick={(e) => e.target === e.currentTarget && setOpen(false)}
    >
      <div
        className="relative w-full max-w-4xl bg-[var(--bg)] rounded-3xl overflow-hidden shadow-2xl border border-[var(--border)] grid md:grid-cols-[1fr,1.1fr]"
        role="dialog"
        aria-labelledby="gift-popup-title"
      >
        <button
          data-testid="exit-intent-close"
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-[var(--primary)] shadow-sm"
          aria-label="Close"
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>

        {/* Visual side */}
        <div className="hidden md:block relative bg-[var(--primary)]">
          <img
            src={ASSETS.opening}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--primary)]/95 via-[var(--primary)]/70 to-[var(--primary)]/40" />
          <div className="relative p-10 h-full flex flex-col justify-between text-[var(--bg)]">
            <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-[var(--accent)]/95 text-white text-[11px] uppercase tracking-[0.24em]">
              <Gift className="w-3.5 h-3.5" strokeWidth={2} />
              {t("New Patient Offer")}
            </div>
            <div>
              <div className="font-mono-eyebrow text-[var(--accent)]">
                {t("Wait — before you go")}
              </div>
              <h2
                id="gift-popup-title"
                className="font-serif-display text-[42px] md:text-[52px] leading-[1.02] tracking-tight mt-3"
              >
                {t("Get a")} <span className="italic text-[var(--accent)]">$20</span> {t("Amazon gift card.")}
              </h2>
              <p className="mt-5 text-[15px] text-white/85 leading-relaxed max-w-md">
                {t(
                  "Book your first visit with Smiles of Loudoun. After your appointment, we'll email you a $20 Amazon eGift card as our thank-you."
                )}
              </p>
            </div>
            <p className="text-xs text-white/60">
              {t("New patients only. One gift per household.")}
            </p>
          </div>
        </div>

        {/* Form side */}
        <div className="p-8 md:p-10 max-h-[90vh] overflow-y-auto">
          {status !== "success" ? (
            <>
              <div className="md:hidden inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--accent)] text-white text-[11px] uppercase tracking-[0.24em] mb-4">
                <Gift className="w-3.5 h-3.5" strokeWidth={2} />
                {t("New Patient Offer")}
              </div>
              <h3 className="md:hidden font-serif-display text-3xl leading-tight text-[var(--primary)]">
                {t("Get a")} <span className="italic text-[var(--accent)]">$20</span> {t("Amazon gift card.")}
              </h3>

              <p className="text-sm text-[var(--text-muted)] mb-5 md:mb-6 md:mt-0 mt-3">
                {t("Reserve your gift below — we'll be in touch within one business day.")}
              </p>

              <form onSubmit={submit} className="space-y-4">
                <Field
                  label={t("Full name")}
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                  testid="gift-input-name"
                  required
                />
                <Field
                  type="email"
                  label={t("Email")}
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                  testid="gift-input-email"
                  required
                />
                <Field
                  type="tel"
                  label={t("Phone")}
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                  testid="gift-input-phone"
                  required
                />

                {status === "error" && (
                  <p className="text-sm text-red-600" data-testid="gift-error">
                    {t("Couldn't send. Please call")} {PRACTICE.phone}.
                    {errMsg ? ` (${errMsg})` : ""}
                  </p>
                )}

                <button
                  data-testid="gift-submit"
                  type="submit"
                  disabled={status === "sending"}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[var(--accent)] text-white font-medium hover:bg-[var(--accent-hover)] disabled:opacity-60 transition-colors"
                >
                  <Gift className="w-4 h-4" strokeWidth={2} />
                  {status === "sending"
                    ? t("Reserving…")
                    : t("Reserve my gift card")}
                </button>

                <button
                  type="button"
                  data-testid="gift-dismiss"
                  onClick={() => setOpen(false)}
                  className="w-full text-center text-xs text-[var(--text-muted)] hover:text-[var(--primary)] underline underline-offset-4"
                >
                  {t("No thanks, I'll pass")}
                </button>
              </form>
            </>
          ) : (
            <div className="py-6 text-center" data-testid="gift-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-[var(--accent)]/15 flex items-center justify-center">
                <Check className="w-7 h-7 text-[var(--accent)]" strokeWidth={2.4} />
              </div>
              <h3 className="font-serif-display text-3xl mt-5 text-[var(--primary)]">
                {t("You're on the list.")}
              </h3>
              <p className="mt-3 text-[var(--text-muted)] leading-relaxed">
                {t(
                  "We opened our booking page in a new tab so you can pick your appointment time. After your visit, your $20 Amazon eGift card arrives by email.",
                )}
              </p>
              <a
                data-testid="gift-open-booking"
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-colors"
              >
                {t("Open booking page")}
              </a>
              <button
                data-testid="gift-close-success"
                onClick={() => setOpen(false)}
                className="mt-3 block mx-auto px-6 py-3 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)] transition-colors"
              >
                {t("Close")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">
        {label}
        {required ? " *" : ""}
      </span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[var(--surface)] border border-[var(--border)] focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 text-[var(--primary)]"
      />
    </label>
  );
}
